import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../../services/firebase';
import { User, Lock, Globe, Shield, Building2, Users, Upload, X, DollarSign, CheckCircle } from 'lucide-react';
import './Settings.css';
import './PlanSelector.css';

const Settings = () => {
  const { i18n } = useTranslation();
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Preferences
  const [preferences, setPreferences] = useState({
    language: 'pt-BR',
    notifications: true,
    emailNotifications: true,
    theme: 'light'
  });
  
  // System settings (for root - Mercado Pago global config)
  const [systemSettings, setSystemSettings] = useState({
    mercadoPagoAccessToken: '',
    mercadoPagoPublicKey: '',
    defaultPlan: 'basic',
    systemFeeBasic: 3,
    systemFeePro: 2,
    systemFeeEnterprise: 1,
    enterpriseFreeTransactions: 10,
    // Comissões padrão por tipo de serviço (definidas pelo root)
    defaultServiceCommissions: {
      boat: 10,
      tour: 15,
      transfer: 8,
      pousada: 12
    },
    // Definições de recursos por plano (root pode editar)
    planFeatures: {
      basic: {
        maxReservationsPerMonth: 100,
        advancedReports: false,
        apiAccess: false,
        supportLevel: 'standard'
      },
      pro: {
        maxReservationsPerMonth: null,
        advancedReports: true,
        apiAccess: false,
        supportLevel: 'priority'
      },
      enterprise: {
        maxReservationsPerMonth: null,
        advancedReports: true,
        apiAccess: true,
        supportLevel: 'dedicated',
        freeTransactionsPerMonth: 10
      }
    }
  });

  // Pricing controlled by root (monthly / annual)
  const [systemPricing, setSystemPricing] = useState({
    basicMonthly: 49,
    basicAnnual: 490,
    proMonthly: 99,
    proAnnual: 990,
    enterpriseMonthly: 199,
    enterpriseAnnual: 1990
  });
  
  // Company settings (for admin)
  const [companySettings, setCompanySettings] = useState({
    companyName: '',
    address: '',
    phone: '',
    email: '',
    timezone: 'America/Sao_Paulo',
    logoUrl: '',
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    // Plan setting (moved from Mercado Pago)
    plan: 'basic',
    monthlyTransactions: 0,
    lastTransactionReset: null
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (userData) {
      setProfileData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || ''
      });
      setPreferences({
        language: userData.language || 'pt-BR',
        notifications: userData.notifications !== false,
        emailNotifications: userData.emailNotifications !== false,
        theme: userData.theme || 'light'
      });
      
      // Carregar configurações do sistema se for root
      if (userData.role === 'root') {
        loadSystemSettings();
        loadPricing();
      }
      
      // Carregar dados da empresa se for admin ou root
      if ((userData.role === 'admin' || userData.role === 'root') && userData.companyId) {
        loadCompanyData();
      }
    }
  }, [userData]);

  const loadSystemSettings = async () => {
    try {
      const systemRef = doc(db, 'systemConfig', 'mercadoPago');
      const systemSnap = await getDoc(systemRef);
      
      if (systemSnap.exists()) {
        const data = systemSnap.data();
        setSystemSettings({
          mercadoPagoAccessToken: data.mercadoPagoAccessToken || '',
          mercadoPagoPublicKey: data.mercadoPagoPublicKey || '',
          defaultPlan: data.defaultPlan || 'basic',
          systemFeeBasic: data.systemFeeBasic || 3,
          systemFeePro: data.systemFeePro || 2,
          systemFeeEnterprise: data.systemFeeEnterprise || 1,
          enterpriseFreeTransactions: data.enterpriseFreeTransactions || 10,
          defaultServiceCommissions: data.defaultServiceCommissions || {
            boat: 10,
            tour: 15,
            transfer: 8,
            pousada: 12
          },
          planFeatures: data.planFeatures || {
            basic: { maxReservationsPerMonth: 100, advancedReports: false, apiAccess: false, supportLevel: 'standard' },
            pro: { maxReservationsPerMonth: null, advancedReports: true, apiAccess: false, supportLevel: 'priority' },
            enterprise: { maxReservationsPerMonth: null, advancedReports: true, apiAccess: true, supportLevel: 'dedicated', freeTransactionsPerMonth: 10 }
          }
        });
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  const loadPricing = async () => {
    try {
      const pricingRef = doc(db, 'systemConfig', 'pricing');
      const pricingSnap = await getDoc(pricingRef);
      if (pricingSnap.exists()) {
        const data = pricingSnap.data();
        setSystemPricing({
          basicMonthly: data.basicMonthly || systemPricing.basicMonthly,
          basicAnnual: data.basicAnnual || systemPricing.basicAnnual,
          proMonthly: data.proMonthly || systemPricing.proMonthly,
          proAnnual: data.proAnnual || systemPricing.proAnnual,
          enterpriseMonthly: data.enterpriseMonthly || systemPricing.enterpriseMonthly,
          enterpriseAnnual: data.enterpriseAnnual || systemPricing.enterpriseAnnual
        });
      }
    } catch (err) {
      console.error('Error loading pricing:', err);
    }
  };

  const loadCompanyData = async () => {
    if (!userData?.companyId) {
      console.error('Company ID not found in userData');
      return;
    }
    
    try {
      const companyRef = doc(db, 'companies', userData.companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        const data = companySnap.data();
        setCompanySettings({
          companyName: data.name || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          timezone: data.timezone || 'America/Sao_Paulo',
          logoUrl: data.logoUrl || '',
          primaryColor: data.primaryColor || '#667eea',
          secondaryColor: data.secondaryColor || '#764ba2',
          plan: data.plan || 'basic',
          monthlyTransactions: data.monthlyTransactions || 0,
          lastTransactionReset: data.lastTransactionReset || null
        });
        
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
        }
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showMessage('error', 'A imagem deve ter no máximo 2MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Apenas imagens são permitidas');
        return;
      }
      
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setCompanySettings({ ...companySettings, logoUrl: '' });
  };

  const uploadLogo = async () => {
    if (!logoFile) return companySettings.logoUrl;
    
    if (!userData?.companyId) {
      showMessage('error', 'ID da empresa não encontrado. Faça login novamente.');
      return companySettings.logoUrl || '';
    }
    
    setUploadingLogo(true);
    try {
      const timestamp = Date.now();
      const logoRef = ref(storage, `companies/${userData.companyId}/logo_${timestamp}`);
      await uploadBytes(logoRef, logoFile);
      const logoUrl = await getDownloadURL(logoRef);
      return logoUrl;
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      showMessage('error', 'Erro ao fazer upload do logo. Firebase Storage pode não estar configurado.');
      return companySettings.logoUrl || '';
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        name: profileData.name,
        phone: profileData.phone,
        updatedAt: new Date()
      });
      
      showMessage('success', 'Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage('error', 'Erro ao atualizar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'As senhas não coincidem.');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      showMessage('error', 'A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    
    setLoading(true);
    
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);
      
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage('success', 'Senha alterada com sucesso!');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        showMessage('error', 'Senha atual incorreta.');
      } else {
        showMessage('error', 'Erro ao alterar senha. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        language: preferences.language,
        notifications: preferences.notifications,
        emailNotifications: preferences.emailNotifications,
        theme: preferences.theme,
        updatedAt: new Date()
      });
      
      i18n.changeLanguage(preferences.language);
      showMessage('success', 'Preferências atualizadas com sucesso!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      showMessage('error', 'Erro ao atualizar preferências. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySettingsUpdate = async (e) => {
    e.preventDefault();
    
    if (!userData?.companyId) {
      showMessage('error', 'ID da empresa não encontrado. Faça login novamente.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Fazer upload do logo se houver
      let logoUrl = companySettings.logoUrl;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }
      
      const companyRef = doc(db, 'companies', userData.companyId);
      await updateDoc(companyRef, {
        name: companySettings.companyName,
        address: companySettings.address,
        phone: companySettings.phone,
        email: companySettings.email,
        timezone: companySettings.timezone,
        logoUrl: logoUrl,
        primaryColor: companySettings.primaryColor,
        secondaryColor: companySettings.secondaryColor,
        plan: companySettings.plan,
        monthlyTransactions: companySettings.monthlyTransactions,
        lastTransactionReset: companySettings.lastTransactionReset,
        updatedAt: new Date()
      });
      
      showMessage('success', 'Configurações da empresa atualizadas com sucesso!');
    } catch (error) {
      console.error('Error updating company settings:', error);
      showMessage('error', 'Erro ao atualizar configurações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const systemRef = doc(db, 'systemConfig', 'mercadoPago');
      await updateDoc(systemRef, {
        mercadoPagoAccessToken: systemSettings.mercadoPagoAccessToken,
        mercadoPagoPublicKey: systemSettings.mercadoPagoPublicKey,
        defaultPlan: systemSettings.defaultPlan,
        systemFeeBasic: systemSettings.systemFeeBasic,
        systemFeePro: systemSettings.systemFeePro,
        systemFeeEnterprise: systemSettings.systemFeeEnterprise,
        enterpriseFreeTransactions: systemSettings.enterpriseFreeTransactions,
        defaultServiceCommissions: systemSettings.defaultServiceCommissions,
        planFeatures: systemSettings.planFeatures,
        updatedAt: new Date()
      }).catch(async (error) => {
        // Se não existe, criar
        if (error.code === 'not-found') {
          const { setDoc } = await import('firebase/firestore');
          await setDoc(systemRef, {
            mercadoPagoAccessToken: systemSettings.mercadoPagoAccessToken,
            mercadoPagoPublicKey: systemSettings.mercadoPagoPublicKey,
            defaultPlan: systemSettings.defaultPlan,
            systemFeeBasic: systemSettings.systemFeeBasic,
            systemFeePro: systemSettings.systemFeePro,
            systemFeeEnterprise: systemSettings.systemFeeEnterprise,
            enterpriseFreeTransactions: systemSettings.enterpriseFreeTransactions,
            defaultServiceCommissions: systemSettings.defaultServiceCommissions,
            planFeatures: systemSettings.planFeatures,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        } else {
          throw error;
        }
      });
      
      showMessage('success', 'Configurações do sistema atualizadas com sucesso!');
      // Salvar preços (monthly / annual)
      try {
        const pricingRef = doc(db, 'systemConfig', 'pricing');
        await updateDoc(pricingRef, {
          basicMonthly: parseFloat(systemPricing.basicMonthly),
          basicAnnual: parseFloat(systemPricing.basicAnnual),
          proMonthly: parseFloat(systemPricing.proMonthly),
          proAnnual: parseFloat(systemPricing.proAnnual),
          enterpriseMonthly: parseFloat(systemPricing.enterpriseMonthly),
          enterpriseAnnual: parseFloat(systemPricing.enterpriseAnnual),
          updatedAt: new Date()
        }).catch(async (error) => {
          if (error.code === 'not-found') {
            const { setDoc } = await import('firebase/firestore');
            await setDoc(pricingRef, {
              basicMonthly: parseFloat(systemPricing.basicMonthly),
              basicAnnual: parseFloat(systemPricing.basicAnnual),
              proMonthly: parseFloat(systemPricing.proMonthly),
              proAnnual: parseFloat(systemPricing.proAnnual),
              enterpriseMonthly: parseFloat(systemPricing.enterpriseMonthly),
              enterpriseAnnual: parseFloat(systemPricing.enterpriseAnnual),
              createdAt: new Date(),
              updatedAt: new Date()
            });
          } else {
            throw error;
          }
        });
      } catch (err) {
        console.error('Error saving pricing:', err);
      }
    } catch (error) {
      console.error('Error updating system settings:', error);
      showMessage('error', 'Erro ao atualizar configurações do sistema. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Perfil', icon: User, roles: ['root', 'admin', 'collaborator'] },
    { id: 'password', label: 'Senha', icon: Lock, roles: ['root', 'admin', 'collaborator'] },
    { id: 'preferences', label: 'Preferências', icon: Globe, roles: ['root', 'admin', 'collaborator'] },
    { id: 'system', label: 'Sistema', icon: DollarSign, roles: ['root'] },
    { id: 'company', label: 'Empresa', icon: Building2, roles: ['root', 'admin'] },
    { id: 'security', label: 'Segurança', icon: Shield, roles: ['root'] }
  ];

  const visibleTabs = tabs.filter(tab => tab.roles.includes(userData?.role));

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Configurações</h1>
        <p>Gerencie suas preferências e informações da conta</p>
      </div>

      {message.text && (
        <div className={`settings-message settings-message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-content">
        <div className="settings-tabs">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="settings-panel">
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>Informações do Perfil</h2>
              <form onSubmit={handleProfileUpdate}>
                <div className="form-group">
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className="input-disabled"
                  />
                  <small>O email não pode ser alterado</small>
                </div>
                
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                
                <div className="form-group">
                  <label>Tipo de Conta</label>
                  <input
                    type="text"
                    value={userData?.role === 'root' ? 'Administrador Root' : userData?.role === 'admin' ? 'Administrador' : 'Colaborador'}
                    disabled
                    className="input-disabled"
                  />
                </div>
                
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="settings-section">
              <h2>Alterar Senha</h2>
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Senha Atual</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Nova Senha</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                  <small>Mínimo de 6 caracteres</small>
                </div>
                
                <div className="form-group">
                  <label>Confirmar Nova Senha</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
                
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h2>Preferências</h2>
              <form onSubmit={handlePreferencesUpdate}>
                <div className="form-group">
                  <label>Idioma</label>
                  <select
                    value={preferences.language}
                    onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                  >
                    <option value="pt-BR">Português (Brasil)</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </div>
                
                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.notifications}
                      onChange={(e) => setPreferences({ ...preferences, notifications: e.target.checked })}
                    />
                    <span>Notificações do Sistema</span>
                  </label>
                  <small>Receba notificações sobre atividades importantes</small>
                </div>
                
                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.emailNotifications}
                      onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
                    />
                    <span>Notificações por Email</span>
                  </label>
                  <small>Receba emails sobre reservas e atualizações</small>
                </div>
                
                <div className="form-group">
                  <label>Tema</label>
                  <select
                    value={preferences.theme}
                    onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                  >
                    <option value="light">Claro</option>
                    <option value="dark">Escuro</option>
                    <option value="auto">Automático</option>
                  </select>
                </div>
                
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar Preferências'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'system' && userData?.role === 'root' && (
            <div className="settings-section">
              <h2>Configurações do Sistema - Mercado Pago</h2>
              <p className="settings-description">
                Configure o Mercado Pago globalmente para todas as empresas do sistema.
              </p>
              <form onSubmit={handleSystemSettingsUpdate}>
                
                <div className="form-group">
                  <label>Access Token do Mercado Pago</label>
                  <input
                    type="password"
                    value={systemSettings.mercadoPagoAccessToken}
                    onChange={(e) => setSystemSettings({ ...systemSettings, mercadoPagoAccessToken: e.target.value })}
                    placeholder="APP_USR-XXXXXXXXXXXXXXX"
                  />
                  <small>
                    Token de acesso obtido no{' '}
                    <a href="https://www.mercadopago.com.br/developers/panel" target="_blank" rel="noopener noreferrer">
                      painel de desenvolvedores do Mercado Pago
                    </a>
                    . Este token será usado por todas as empresas.
                  </small>
                </div>

                <div className="form-group">
                  <label>Public Key do Mercado Pago (opcional)</label>
                  <input
                    type="text"
                    value={systemSettings.mercadoPagoPublicKey}
                    onChange={(e) => setSystemSettings({ ...systemSettings, mercadoPagoPublicKey: e.target.value })}
                    placeholder="APP_USR-XXXXXXXXXXXXXXX"
                  />
                  <small>Chave pública para integrações frontend (opcional).</small>
                </div>

                <div className="settings-subsection">
                  <h3>Planos e Tarifação</h3>
                  <p className="subsection-description">
                    Configure as taxas cobradas pelo sistema para cada plano empresarial.
                  </p>
                  
                  <div className="form-group">
                    <label>Plano Padrão</label>
                    <select
                      value={systemSettings.defaultPlan}
                      onChange={(e) => setSystemSettings({ ...systemSettings, defaultPlan: e.target.value })}
                    >
                      <option value="basic">Básico</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    <small>Plano atribuído automaticamente para novas empresas.</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Taxa Plano Básico (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={systemSettings.systemFeeBasic}
                        onChange={(e) => setSystemSettings({ ...systemSettings, systemFeeBasic: parseFloat(e.target.value) })}
                      />
                      <small>Taxa cobrada por transação no plano básico.</small>
                    </div>

                    <div className="form-group">
                      <label>Taxa Plano Pro (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={systemSettings.systemFeePro}
                        onChange={(e) => setSystemSettings({ ...systemSettings, systemFeePro: parseFloat(e.target.value) })}
                      />
                      <small>Taxa cobrada por transação no plano pro.</small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Taxa Plano Enterprise (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={systemSettings.systemFeeEnterprise}
                        onChange={(e) => setSystemSettings({ ...systemSettings, systemFeeEnterprise: parseFloat(e.target.value) })}
                      />
                      <small>Taxa cobrada após transações gratuitas.</small>
                    </div>

                    <div className="form-group">
                      <label>Transações Grátis (Enterprise)</label>
                      <input
                        type="number"
                        min="0"
                        value={systemSettings.enterpriseFreeTransactions}
                        onChange={(e) => setSystemSettings({ ...systemSettings, enterpriseFreeTransactions: parseInt(e.target.value) })}
                      />
                      <small>Quantidade de transações gratuitas por mês.</small>
                    </div>
                  </div>

                  <div className="info-box">
                    <span className="info-icon">ℹ️</span>
                    <div>
                      <strong>Sobre os Planos</strong>
                      <p>
                        <strong>Básico:</strong> Taxa fixa de {systemSettings.systemFeeBasic}% por transação.<br/>
                        <strong>Pro:</strong> Taxa fixa de {systemSettings.systemFeePro}% por transação.<br/>
                        <strong>Enterprise:</strong> {systemSettings.enterpriseFreeTransactions} transações grátis/mês, depois {systemSettings.systemFeeEnterprise}% por transação.
                      </p>
                    </div>
                  </div>

                  <div className="settings-subsection">
                    <h3>Comissões por Tipo de Serviço</h3>
                    <p className="subsection-description">Defina as porcentagens de comissão padrão para cada tipo de serviço. Estas serão usadas como referência no sistema.</p>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Barco (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={systemSettings.defaultServiceCommissions?.boat || 10}
                          onChange={(e) => setSystemSettings({
                            ...systemSettings,
                            defaultServiceCommissions: {
                              ...systemSettings.defaultServiceCommissions,
                              boat: parseFloat(e.target.value)
                            }
                          })}
                        />
                        <small>Comissão padrão para serviços de barco.</small>
                      </div>

                      <div className="form-group">
                        <label>Passeio/Tour (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={systemSettings.defaultServiceCommissions?.tour || 15}
                          onChange={(e) => setSystemSettings({
                            ...systemSettings,
                            defaultServiceCommissions: {
                              ...systemSettings.defaultServiceCommissions,
                              tour: parseFloat(e.target.value)
                            }
                          })}
                        />
                        <small>Comissão padrão para passeios e tours.</small>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Transfer (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={systemSettings.defaultServiceCommissions?.transfer || 8}
                          onChange={(e) => setSystemSettings({
                            ...systemSettings,
                            defaultServiceCommissions: {
                              ...systemSettings.defaultServiceCommissions,
                              transfer: parseFloat(e.target.value)
                            }
                          })}
                        />
                        <small>Comissão padrão para serviços de transfer.</small>
                      </div>

                      <div className="form-group">
                        <label>Pousada (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={systemSettings.defaultServiceCommissions?.pousada || 12}
                          onChange={(e) => setSystemSettings({
                            ...systemSettings,
                            defaultServiceCommissions: {
                              ...systemSettings.defaultServiceCommissions,
                              pousada: parseFloat(e.target.value)
                            }
                          })}
                        />
                        <small>Comissão padrão para pousadas.</small>
                      </div>
                    </div>

                    <div className="info-box">
                      <span className="info-icon">ℹ️</span>
                      <div>
                        <strong>Sobre as Comissões</strong>
                        <p>
                          Estas são as porcentagens de comissão padrão por tipo de serviço definidas pelo administrador root.
                          Elas servem como referência para o sistema e podem ser usadas para calcular comissões de colaboradores.
                        </p>
                      </div>
                    </div>

                    <div className="settings-subsection">
                      <h3>Recursos por Plano</h3>
                      <p className="subsection-description">Defina limites e recursos que cada plano oferece. Essas regras serão aplicadas ao sistema (limite de reservas, transações grátis, acesso a API, etc.).</p>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Básico - Limite de Reservas / mês</label>
                          <input
                            type="number"
                            min="0"
                            value={systemSettings.planFeatures?.basic?.maxReservationsPerMonth ?? 100}
                            onChange={(e) => setSystemSettings({
                              ...systemSettings,
                              planFeatures: {
                                ...systemSettings.planFeatures,
                                basic: {
                                  ...systemSettings.planFeatures?.basic,
                                  maxReservationsPerMonth: e.target.value ? parseInt(e.target.value) : null
                                }
                              }
                            })}
                          />
                        </div>

                        <div className="form-group">
                          <label>Básico - Suporte</label>
                          <select
                            value={systemSettings.planFeatures?.basic?.supportLevel || 'standard'}
                            onChange={(e) => setSystemSettings({
                              ...systemSettings,
                              planFeatures: {
                                ...systemSettings.planFeatures,
                                basic: {
                                  ...systemSettings.planFeatures?.basic,
                                  supportLevel: e.target.value
                                }
                              }
                            })}
                          >
                            <option value="standard">Standard</option>
                            <option value="priority">Priority</option>
                            <option value="dedicated">Dedicated</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Pro - Limite de Reservas / mês</label>
                          <input
                            type="number"
                            min="0"
                            value={systemSettings.planFeatures?.pro?.maxReservationsPerMonth ?? ''}
                            onChange={(e) => setSystemSettings({
                              ...systemSettings,
                              planFeatures: {
                                ...systemSettings.planFeatures,
                                pro: {
                                  ...systemSettings.planFeatures?.pro,
                                  maxReservationsPerMonth: e.target.value ? parseInt(e.target.value) : null
                                }
                              }
                            })}
                          />
                        </div>

                        <div className="form-group">
                          <label>Pro - Suporte</label>
                          <select
                            value={systemSettings.planFeatures?.pro?.supportLevel || 'priority'}
                            onChange={(e) => setSystemSettings({
                              ...systemSettings,
                              planFeatures: {
                                ...systemSettings.planFeatures,
                                pro: {
                                  ...systemSettings.planFeatures?.pro,
                                  supportLevel: e.target.value
                                }
                              }
                            })}
                          >
                            <option value="standard">Standard</option>
                            <option value="priority">Priority</option>
                            <option value="dedicated">Dedicated</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Enterprise - Transações grátis / mês</label>
                          <input
                            type="number"
                            min="0"
                            value={systemSettings.planFeatures?.enterprise?.freeTransactionsPerMonth ?? systemSettings.enterpriseFreeTransactions}
                            onChange={(e) => setSystemSettings({
                              ...systemSettings,
                              planFeatures: {
                                ...systemSettings.planFeatures,
                                enterprise: {
                                  ...systemSettings.planFeatures?.enterprise,
                                  freeTransactionsPerMonth: e.target.value ? parseInt(e.target.value) : 0
                                }
                              }
                            })}
                          />
                        </div>

                        <div className="form-group">
                          <label>Enterprise - API Access</label>
                          <div>
                            <label>
                              <input
                                type="checkbox"
                                checked={!!systemSettings.planFeatures?.enterprise?.apiAccess}
                                onChange={(e) => setSystemSettings({
                                  ...systemSettings,
                                  planFeatures: {
                                    ...systemSettings.planFeatures,
                                    enterprise: {
                                      ...systemSettings.planFeatures?.enterprise,
                                      apiAccess: e.target.checked
                                    }
                                  }
                                })}
                              />{' '}
                              Habilitar API Access
                            </label>
                          </div>
                        </div>
                      </div>

                    </div>

                    <div className="settings-subsection">
                    <h3>Preços (mensal / anual)</h3>
                    <p className="subsection-description">Defina os valores de assinatura que serão exibidos na landing page.</p>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Básico - Mensal (R$)</label>
                        <input type="number" value={systemPricing.basicMonthly} onChange={(e) => setSystemPricing({ ...systemPricing, basicMonthly: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Básico - Anual (R$)</label>
                        <input type="number" value={systemPricing.basicAnnual} onChange={(e) => setSystemPricing({ ...systemPricing, basicAnnual: e.target.value })} />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Pro - Mensal (R$)</label>
                        <input type="number" value={systemPricing.proMonthly} onChange={(e) => setSystemPricing({ ...systemPricing, proMonthly: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Pro - Anual (R$)</label>
                        <input type="number" value={systemPricing.proAnnual} onChange={(e) => setSystemPricing({ ...systemPricing, proAnnual: e.target.value })} />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Enterprise - Mensal (R$)</label>
                        <input type="number" value={systemPricing.enterpriseMonthly} onChange={(e) => setSystemPricing({ ...systemPricing, enterpriseMonthly: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Enterprise - Anual (R$)</label>
                        <input type="number" value={systemPricing.enterpriseAnnual} onChange={(e) => setSystemPricing({ ...systemPricing, enterpriseAnnual: e.target.value })} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Salvando...' : 'Salvar Configurações do Sistema'}
                </button>
              </div>
              </form>
            </div>
          )}

          {activeTab === 'company' && (userData?.role === 'admin' || userData?.role === 'root') && (
            <div className="settings-section">
              <h2>Configurações da Empresa</h2>
              
              {!userData?.companyId ? (
                <div className="alert alert-warning">
                  <p>⚠️ ID da empresa não encontrado. Por favor, faça logout e login novamente.</p>
                  <p>Se o problema persistir, contate o suporte técnico.</p>
                </div>
              ) : (
                <form onSubmit={handleCompanySettingsUpdate}>
                
                {/* Logo da Empresa */}
                <div className="form-group">
                  <label>Logo da Empresa</label>
                  <div className="logo-upload-container">
                    {logoPreview ? (
                      <div className="logo-preview">
                        <img src={logoPreview} alt="Logo" />
                        <button 
                          type="button" 
                          className="btn-remove-logo"
                          onClick={handleRemoveLogo}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="logo-upload-placeholder">
                        <Upload size={32} />
                        <p>Nenhum logo selecionado</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      style={{ display: 'none' }}
                      id="logo-upload"
                    />
                    <label htmlFor="logo-upload" className="btn-secondary" style={{ cursor: 'pointer' }}>
                      {logoPreview ? 'Alterar Logo' : 'Selecionar Logo'}
                    </label>
                    <small>Imagem PNG ou JPG, máximo 2MB. Será exibido nos comprovantes.</small>
                  </div>
                </div>

                {/* Cores da Empresa */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Cor Primária</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={companySettings.primaryColor}
                        onChange={(e) => setCompanySettings({ ...companySettings, primaryColor: e.target.value })}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={companySettings.primaryColor}
                        onChange={(e) => setCompanySettings({ ...companySettings, primaryColor: e.target.value })}
                        placeholder="#667eea"
                        className="color-text-input"
                      />
                    </div>
                    <small>Cor principal usada nos comprovantes</small>
                  </div>
                  
                  <div className="form-group">
                    <label>Cor Secundária</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={companySettings.secondaryColor}
                        onChange={(e) => setCompanySettings({ ...companySettings, secondaryColor: e.target.value })}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={companySettings.secondaryColor}
                        onChange={(e) => setCompanySettings({ ...companySettings, secondaryColor: e.target.value })}
                        placeholder="#764ba2"
                        className="color-text-input"
                      />
                    </div>
                    <small>Cor secundária para destaques</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Nome da Empresa</label>
                  <input
                    type="text"
                    value={companySettings.companyName}
                    onChange={(e) => setCompanySettings({ ...companySettings, companyName: e.target.value })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Endereço</label>
                  <input
                    type="text"
                    value={companySettings.address}
                    onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="tel"
                    value={companySettings.phone}
                    onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email de Contato</label>
                  <input
                    type="email"
                    value={companySettings.email}
                    onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Fuso Horário</label>
                  <select
                    value={companySettings.timezone}
                    onChange={(e) => setCompanySettings({ ...companySettings, timezone: e.target.value })}
                  >
                    <option value="America/Sao_Paulo">Brasília (UTC-3)</option>
                    <option value="America/New_York">Nova York (UTC-5)</option>
                    <option value="Europe/London">Londres (UTC+0)</option>
                    <option value="Asia/Tokyo">Tóquio (UTC+9)</option>
                  </select>
                </div>

                <div className="settings-subsection">
                  <h3>Plano da Empresa</h3>
                  <p className="subsection-description">
                    Selecione o plano que define a taxa cobrada pelo sistema em cada transação via Mercado Pago.
                  </p>
                  
                  <div className="plan-selector-settings">
                    {/* Basic Plan */}
                    <div 
                      className={`plan-card-settings ${companySettings.plan === 'basic' ? 'selected' : ''}`}
                      onClick={() => setCompanySettings({ ...companySettings, plan: 'basic' })}
                    >
                      <div className="plan-card-settings-header">
                        <CheckCircle />
                        <h3>Básico</h3>
                      </div>
                      <div className="plan-card-settings-price">
                        {systemPricing.basicMonthly > 0 ? (
                          <>
                            R$ {systemPricing.basicMonthly} <small>/mês</small>
                          </>
                        ) : (
                          'Grátis'
                        )}
                      </div>
                      <p className="plan-card-settings-description">
                        Taxa de <strong>{systemSettings.systemFeeBasic}%</strong> por transação.
                      </p>
                      <ul className="plan-card-settings-features">
                        <li><CheckCircle size={16} /> Ideal para iniciantes</li>
                        <li><CheckCircle size={16} /> Suporte via comunidade</li>
                      </ul>
                    </div>

                    {/* Pro Plan */}
                    <div 
                      className={`plan-card-settings ${companySettings.plan === 'pro' ? 'selected' : ''}`}
                      onClick={() => setCompanySettings({ ...companySettings, plan: 'pro' })}
                    >
                      {companySettings.plan === 'pro' && <div className="popular-badge-settings">Popular</div>}
                      <div className="plan-card-settings-header">
                        <CheckCircle />
                        <h3>Pro</h3>
                      </div>
                      <div className="plan-card-settings-price">
                        R$ {systemPricing.proMonthly} <small>/mês</small>
                      </div>
                      <p className="plan-card-settings-description">
                        Taxa reduzida de <strong>{systemSettings.systemFeePro}%</strong> por transação.
                      </p>
                      <ul className="plan-card-settings-features">
                        <li><CheckCircle size={16} /> Reservas ilimitadas</li>
                        <li><CheckCircle size={16} /> Suporte prioritário</li>
                      </ul>
                    </div>

                    {/* Enterprise Plan */}
                    <div 
                      className={`plan-card-settings ${companySettings.plan === 'enterprise' ? 'selected' : ''}`}
                      onClick={() => setCompanySettings({ ...companySettings, plan: 'enterprise' })}
                    >
                      <div className="plan-card-settings-header">
                        <CheckCircle />
                        <h3>Enterprise</h3>
                      </div>
                      <div className="plan-card-settings-price">
                        R$ {systemPricing.enterpriseMonthly} <small>/mês</small>
                      </div>
                      <p className="plan-card-settings-description">
                        <strong>{systemSettings.enterpriseFreeTransactions}</strong> transações grátis, depois <strong>{systemSettings.systemFeeEnterprise}%</strong>.
                      </p>
                      <ul className="plan-card-settings-features">
                        <li><CheckCircle size={16} /> Tudo do Pro</li>
                        <li><CheckCircle size={16} /> API e integrações</li>
                      </ul>
                    </div>
                  </div>

                  {companySettings.plan === 'enterprise' && (
                    <div className="info-box">
                      <span className="info-icon">ℹ️</span>
                      <div>
                        <strong>Plano Enterprise</strong>
                        <p>
                          Você tem {systemSettings.enterpriseFreeTransactions - (companySettings.monthlyTransactions || 0)} transações gratuitas restantes este mês.
                          {companySettings.lastTransactionReset && (
                            <> Último reset: {new Date(companySettings.lastTransactionReset.toDate ? companySettings.lastTransactionReset.toDate() : companySettings.lastTransactionReset).toLocaleDateString('pt-BR')}</>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                <button type="submit" className="btn-primary" disabled={loading || uploadingLogo}>
                  {loading || uploadingLogo ? 'Salvando...' : 'Salvar Configurações'}
                </button>
              </form>
              )}
            </div>
          )}

          {activeTab === 'security' && userData?.role === 'root' && (
            <div className="settings-section">
              <h2>Segurança e Administração</h2>
              <div className="security-info">
                <div className="security-item">
                  <Shield size={24} />
                  <div>
                    <h3>Autenticação de Dois Fatores</h3>
                    <p>Adicione uma camada extra de segurança à sua conta</p>
                    <button className="btn-secondary">Configurar 2FA</button>
                  </div>
                </div>
                
                <div className="security-item">
                  <Users size={24} />
                  <div>
                    <h3>Sessões Ativas</h3>
                    <p>Gerencie os dispositivos conectados à sua conta</p>
                    <button className="btn-secondary">Ver Sessões</button>
                  </div>
                </div>
                
                <div className="security-item">
                  <Lock size={24} />
                  <div>
                    <h3>Logs de Auditoria</h3>
                    <p>Visualize o histórico de acessos e alterações</p>
                    <button className="btn-secondary">Ver Logs</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
